import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useT, useI18n } from "@/context/I18nContext";
import { isSuperAdmin } from "@/context/AuthContext";
import { rolesForOrgType, labelFor } from "@/lib/roles";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MailPlus, Send, RotateCcw, Ban, ShieldCheck, HeartHandshake, Briefcase } from "lucide-react";

const statusColor = (s) => {
  const st = (s || "").toLowerCase();
  if (st === "accepted" || st === "redeemed") return "bg-sage-100 text-sage-700";
  if (st === "pending" || st === "sent") return "bg-clay-500/10 text-clay-600";
  if (st === "cancelled" || st === "expired") return "bg-cream-100 text-ink-500";
  return "bg-cream-100 text-ink-500";
};

/** Small pill showing the org type + icon. */
function OrgTypeBadge({ type, lang }) {
  const t = String(type || "").toLowerCase();
  if (t === "care" || t === "ggz") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-accents-care/10 text-accents-care border border-accents-care/20 text-[11px] px-2 py-0.5">
      <HeartHandshake className="h-3 w-3" /> {lang === "nl" ? "Zorg (GGZ)" : "Care (GGZ)"}
    </span>
  );
  if (t === "internal" || t === "platform") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-sage-100 text-sage-700 border border-sage-200 text-[11px] px-2 py-0.5">
      <ShieldCheck className="h-3 w-3" /> Platform
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-accents-business/10 text-amber-800 border border-accents-business/20 text-[11px] px-2 py-0.5">
      <Briefcase className="h-3 w-3" /> {lang === "nl" ? "Zakelijk" : "Business"}
    </span>
  );
}

export default function Invitations() {
  const t = useT();
  const { lang } = useI18n();
  const qc = useQueryClient();
  const { roles, platform } = useOutletContext();
  const canPickOrg = isSuperAdmin(roles);

  // Organizations — super_admin/platform_owner sees all, others see their single one.
  const orgs = useQuery({
    queryKey: ["admin-orgs"],
    queryFn: async () => (await api.get("/platform/organizations")).data,
    retry: false,
  });
  const orgList = useMemo(
    () => (Array.isArray(orgs.data) ? orgs.data : orgs.data?.organizations || orgs.data?.items || []),
    [orgs.data]
  );

  // Determine "current" org: for HR admins it's platform.organization_id; for super_admin default to first.
  const initialOrgId =
    platform?.organization_id ||
    platform?.org_id ||
    platform?.org?.id ||
    orgList[0]?.id ||
    "";
  const [orgId, setOrgId] = useState(initialOrgId);
  useEffect(() => {
    if (!orgId && orgList.length) setOrgId(orgList[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgList.length]);

  const selectedOrg = useMemo(
    () => orgList.find((o) => (o.id || o._id) === orgId) || null,
    [orgList, orgId]
  );
  const orgType = String(
    selectedOrg?.type ||
    selectedOrg?.organisation_type ||
    selectedOrg?.organization_type ||
    "business"
  ).toLowerCase();

  const availableRoles = useMemo(() => rolesForOrgType(orgType), [orgType]);

  // Default role changes automatically when org type changes.
  const [role, setRole] = useState(availableRoles[0]?.value || "");
  useEffect(() => {
    setRole(availableRoles[0]?.value || "");
  }, [orgType]); // eslint-disable-line react-hooks/exhaustive-deps

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [teamId, setTeamId] = useState("");
  const [busy, setBusy] = useState(false);

  const invites = useQuery({
    queryKey: ["admin-invites-outbox", orgId],
    queryFn: async () => (await api.get("/platform/invites/outbox", { params: { organization_id: orgId } })).data,
    retry: false,
    enabled: !!orgId,
  });
  const teams = useQuery({
    queryKey: ["admin-teams", orgId],
    queryFn: async () => (await api.get("/platform/teams", { params: { organization_id: orgId } })).data,
    retry: false,
    enabled: !!orgId && orgType === "business",
  });

  const items = Array.isArray(invites.data) ? invites.data : invites.data?.invites || invites.data?.items || [];
  const teamList = Array.isArray(teams.data) ? teams.data : teams.data?.teams || teams.data?.items || [];

  const send = async () => {
    if (!email.trim() || !role) return;
    // Client-side safety net: role MUST be valid for this org type.
    if (!availableRoles.some((r) => r.value === role)) {
      toast.error(lang === "nl"
        ? "Deze rol is niet beschikbaar voor dit organisatietype."
        : "This role isn't available for this organisation type.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/platform/invites", {
        email: email.trim(),
        role,
        organization_id: orgId,
        team_id: orgType === "business" ? (teamId || null) : null,
      });
      setEmail(""); setTeamId(""); setOpen(false);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-invites-outbox", orgId] });
    } catch (e) {
      // Surface backend validation error if it rejected the mismatched role.
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const resend = async (inv) => {
    try {
      await api.post("/platform/invites/resend", { email: inv.email, token: inv.token, id: inv.id });
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-invites-outbox", orgId] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cancel = async (inv) => {
    try {
      await api.post("/platform/invites/cancel", { email: inv.email, token: inv.token, id: inv.id });
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-invites-outbox", orgId] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.invites")}
        title={t("nav.invites")}
        subtitle={t("platform.outbox")}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invites-create-open" className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
                <MailPlus className="h-4 w-4 mr-1" /> {t("platform.inviteMember")}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="invites-create-dialog">
              <DialogHeader><DialogTitle>{t("platform.inviteMember")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                {canPickOrg && orgList.length > 1 && (
                  <div>
                    <Label>{t("nav.organisation")}</Label>
                    <Select value={orgId} onValueChange={setOrgId}>
                      <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-cream-50" data-testid="invites-create-org">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {orgList.map((o) => (
                          <SelectItem key={o.id || o._id} value={o.id || o._id}>
                            {o.name} · {(o.type || o.organisation_type || o.organization_type || "business")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex items-center gap-2 -mt-1">
                  <span className="text-xs text-ink-500">{lang === "nl" ? "Rollen voor" : "Roles for"}:</span>
                  <OrgTypeBadge type={orgType} lang={lang} />
                </div>

                <div>
                  <Label>{t("common.email")}</Label>
                  <Input data-testid="invites-create-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-cream-50" placeholder="person@example.com" />
                </div>

                <div>
                  <Label>{t("common.role")}</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-cream-50" data-testid="invites-create-role">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles.map((r) => (
                        <SelectItem key={r.value} value={r.value} data-testid={`invites-role-${r.value}`}>
                          {lang === "nl" ? r.nl : r.en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {orgType === "business" && teamList.length > 0 && (
                  <div>
                    <Label>{t("nav.teams")} <span className="text-ink-500 text-xs">({t("common.optional")})</span></Label>
                    <Select value={teamId} onValueChange={setTeamId}>
                      <SelectTrigger className="mt-1.5 h-11 rounded-xl bg-cream-50" data-testid="invites-create-team">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {teamList.map((tm) => (
                          <SelectItem key={tm.id} value={tm.id}>{tm.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={send} disabled={busy || !email.trim() || !role} data-testid="invites-create-submit" className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
                  <Send className="h-4 w-4 mr-1.5" /> {busy ? t("common.saving") : t("common.send")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {canPickOrg && orgList.length > 1 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="invites-org-switcher">
          <span className="text-xs text-ink-500">{lang === "nl" ? "Organisatie" : "Organisation"}:</span>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="h-9 rounded-full bg-white border-border max-w-xs" data-testid="invites-org-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {orgList.map((o) => (
                <SelectItem key={o.id || o._id} value={o.id || o._id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <OrgTypeBadge type={orgType} lang={lang} />
        </div>
      )}

      {invites.isLoading && <LoadingCard />}
      {invites.isError && <ErrorState message={errMsg(invites.error)} onRetry={() => invites.refetch()} />}
      {!invites.isLoading && !invites.isError && items.length === 0 && (
        <EmptyState icon={MailPlus} title={t("common.empty")} description={lang === "nl" ? "Nodig je eerste persoon uit om ze hier te zien." : "Invite your first person to see them here."} testId="invites-empty" />
      )}
      <div className="rounded-3xl bg-white shadow-card overflow-hidden" data-testid="invites-list">
        <table className="min-w-full text-sm">
          <thead className="bg-cream-50">
            <tr className="text-left text-ink-500 text-[11px] uppercase tracking-[0.15em]">
              <th className="py-3 px-4">{t("common.email")}</th>
              <th className="py-3 px-4">{t("common.role")}</th>
              <th className="py-3 px-4">{t("common.status")}</th>
              <th className="py-3 px-4">Sent</th>
              <th className="py-3 px-4 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((iv, i) => (
              <tr key={iv.id || iv.token || i} className="border-t border-border" data-testid={`invites-row-${i}`}>
                <td className="py-3 px-4 text-ink-900">{iv.email || "—"}</td>
                <td className="py-3 px-4 text-ink-700">{labelFor(iv.role || "employee", lang)}</td>
                <td className="py-3 px-4">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium ${statusColor(iv.status)}`}>
                    {iv.status || "pending"}
                  </span>
                </td>
                <td className="py-3 px-4 text-ink-500 text-xs">{iv.created_at && new Date(iv.created_at).toLocaleDateString()}</td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" className="rounded-full h-8 border-border" onClick={() => resend(iv)} data-testid={`invites-resend-${i}`}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> {t("common.resend")}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full h-8 border-border text-rust-600" onClick={() => cancel(iv)} data-testid={`invites-cancel-${i}`}>
                      <Ban className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
